import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  // =========================
  // TARGETS
  // =========================
  static targets = [
    "postalCode",
    "streetAddress",
    "neighborhood",
    "city",
    "state",
    "local",
    "geolocation",
  ];

  // =========================
  // VALUES
  // =========================
  static values = {
    url: String,
    validationClasses: Boolean,
  };

  // =========================
  // LIFECYCLE
  // =========================
  connect() {
    this.handleInput = this.handleInput.bind(this);

    this.element.addEventListener("input", this.handleInput);

    // 👇 VALIDAÇÃO INICIAL
    this.validateRequiredFields();
  }

  disconnect() {
    this.element.removeEventListener("input", this.handleInput);
  }

  // =========================
  // EVENTS
  // =========================
  async search(event) {
    event.preventDefault();

    const postalCode = this.postalCodeTarget.value.replace(/\D/g, "");

    if (postalCode.length !== 8) {
      alert("CEP deve conter 8 dígitos");
      this.markInvalid(this.postalCodeTarget);
      return;
    }

    try {
      this.setLoadingState(true);

      const response = await fetch(
        `${this.urlValue}?postal_code=${postalCode}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]')
              .content,
          },
        },
      );

      if (!response.ok) throw new Error("Erro ao buscar CEP");

      const data = await response.json();

      if (data.error) {
        alert(data.error);
        this.markInvalid(this.postalCodeTarget);
        this.setGeolocation(null);
      } else {
        this.populateFields(data);
        this.validateRequiredFields();
      }
    } catch (error) {
      this.setGeolocation(null);
      console.error("Erro:", error);
      alert("Não foi possível buscar o endereço. Tente novamente.");
    } finally {
      this.setLoadingState(false);
    }
  }

  // =========================
  // ADDRESS
  // =========================
  populateFields(data) {
    const address = data.address || {};

    if (address.street_address)
      this.streetAddressTarget.value = address.street_address;

    if (address.neighborhood)
      this.neighborhoodTarget.value = address.neighborhood;

    if (address.city) this.cityTarget.value = address.city;

    if (address.state) this.stateTarget.value = address.state;

    this.localTarget.value = data.formatted_address || "";

    this.setGeolocation(data.geolocation);
  }

  // =========================
  // GEOLOCATION
  // =========================
  setGeolocation(geolocation) {
    if (!this.hasGeolocationTarget) return;

    const hasCoords =
      geolocation && geolocation.lat != null && geolocation.lng != null;

    this.geolocationTarget.value = hasCoords
      ? JSON.stringify({ lat: geolocation.lat, lng: geolocation.lng })
      : "";
  }

  // =========================
  // VALIDATION
  // =========================
  handleInput(event) {
    const field = event.target;

    if (field === this.postalCodeTarget) {
      this.setGeolocation(null);
    }

    if (field.dataset.requiredField === "true") {
      this.validateField(field);
    }
  }

  validateRequiredFields() {
    const fields = this.element.querySelectorAll(
      "[data-required-field='true']",
    );

    fields.forEach((field) => this.validateField(field));
  }

  validateField(field) {
    const valid = field.value && field.value.trim() !== "";

    if (!this.validationClassesValue) return;

    if (valid) {
      field.classList.add("is-valid");
      field.classList.remove("is-invalid");
    } else {
      field.classList.add("is-invalid");
      field.classList.remove("is-valid");
    }
  }

  markInvalid(field) {
    field.classList.add("is-invalid");
    field.classList.remove("is-valid");
  }

  // =========================
  // UI
  // =========================
  setLoadingState(loading) {
    const button = this.element.querySelector(
      '[data-action="cep-lookup#search"]',
    );

    if (!button) return;

    button.disabled = loading;
    button.textContent = loading ? "Buscando..." : "Pesquisar";
  }
}
